import contactActions from 'actions/contact';
import { CloseableContent } from 'dyl-components';
import React, { useContext, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Dropdown, Button, Popup, Header, Ref } from 'semantic-ui-react';

import ConvertContactForm from 'shared/forms/ConvertContactForm';
import ConvertToOpportunityForm from 'shared/forms/ConvertToOpportunityForm';

import sequenceTasksActions from 'actions/sequence_tasks';

import LostForm from 'shared/forms/LostForm';
import ConvertNewCustomerPopup from 'shared/ConvertNewCustomerPopup';
import { ACTIONS, QuoteBuilderContext } from 'shared/context/QuoteBuilderProvider';
import UpsellCustomerPopup from 'shared/UpsellCustomerPopup';
import quoteActions from 'actions/quote';
import OutstandingInvoicesForm from 'shared/forms/OutstandingInvoicesForm';
import BulkActionsProvider from 'shared/context/BulkActionsProvider';
import ConvertToNewCustomerForm from 'shared/forms/ConvertToNewCustomerForm';
import ConvertAccountToCustomerForm from 'shared/forms/ConvertAccountToCustomerForm';
import RestrictedView from 'shared/RestrictedView';
import { USER_PERMISSIONS } from 'dyl-components';

const ConversionControls = () => {
    const modalRef = React.createRef();

    const dispatch = useDispatch();
    const { account_id, contact_id, contactName, canConvertToLead, canConvertToOpportunity, account_stage, master_source, secondary_source, pipeline_id, account_is_a_customer, account, mainPhone, mainEmail, mainLocation, assignees } = useSelector(state => {
        const account_stage = state.contact.current_stage?.account_stage || 'contact';
        const { master_source, secondary_source } = state.contact.current_stage || {};
        const locations = state.contact.contact?.locations.slice(0) || [];
        const phones = state.contact.phone || [];
        const emails = state.contact.email || [];
        const mainLocation = locations.find(location => location.billing) || locations.find(location => location.shipping) || locations.find(location => location.main) || locations[0];
        return ({
            account_id: Number(state.contact.account_id),
            contact_id: Number(state.contact.contact_id),

            contactName: `${state.contact.contact.first_name} ${state.contact.contact.last_name} ${state.contact.contact.suffix || ''}`,

            canConvertToLead: account_stage === 'contact',
            canConvertToOpportunity: account_stage === 'contact' || account_stage === 'lead',
            account_stage,

            master_source,
            secondary_source: secondary_source?.id || null,

            pipeline_id: state.contact.current_stage?.pipeline_id,
            account_is_a_customer: state.contact.account?.has_customer,
            account: state.contact.account,

            phones: state.contact.phone || null,
            emails: state.contact.email || null,
            mainPhone: phones.find(phone => phone.main) || phones[0],
            mainEmail: emails.find(email => email.maim) || emails[0],
            mainLocation,
            assignees: state.contact.contact.assignees
        })
    });

    const [conversionModalOpen, setConversionModalOpen] = useState('');
    const [isConversionFormModified, setIsConversionFormModified] = useState(false);

    const [lostFormOpen, setLostFormOpen] = useState(false);
    const [isLostFormModified, setIsLostFormModified] = useState(false);

    const onClose = (e) => {
        const clickedElement = e?.target?.className;
        const isDropdownItemClicked = clickedElement === "item" || clickedElement === "text";
        if (!isDropdownItemClicked) {
            setConversionModalOpen('');
            setIsConversionFormModified(false);
        }
    }

    const onCloseLostForm = () => {
        setLostFormOpen(false);
    }

    const refresh = () => {
        dispatch(contactActions.readContact(contact_id));
        dispatch(sequenceTasksActions.readContactTasks(contact_id));
    };

    const { onOpenAddProducts, onEditQuote } = useContext(QuoteBuilderContext);
    const canUpsell = account_stage !== 'lead';
    const recentOpportunityQuote = useSelector((state) => state.quote.recentOpportunityQuote);
    const recentActiveQuoteId = pipeline_id ? recentOpportunityQuote?.recent_active_quote_id : undefined;
    const recentInactiveQuoteId = recentOpportunityQuote?.recent_inactive_quote_id;
    const isUpsellingFromQuote = !!(canUpsell && (recentActiveQuoteId || recentInactiveQuoteId));

    const isPausedCustomer = account?.customer_status === 'paused';
    const isDeactivatedCustomer = account?.customer_status === 'canceled' || isPausedCustomer;
    const onUpsell = () => {
        if (account_stage === "opportunity" && isUpsellingFromQuote) {
            onEditQuote(recentInactiveQuoteId || recentActiveQuoteId, ACTIONS.UPSELL);
        } else {
            onOpenAddProducts({ isUpsell: true });
        }
    }

    const getPopupContent = () => {
        switch (conversionModalOpen) {
            case 'lead':
                return (
                    <React.Fragment>
                        <Header color='primary'>Convert to Lead</Header>
                        <ConvertContactForm
                            size='small'
                            account_id={account_id}
                            contact_id={contact_id}
                            onClose={onClose}
                            refresh={refresh}
                            onModify={(isModified) => { setIsConversionFormModified(isModified) }}

                        />
                    </React.Fragment>
                )
            case 'opportunity':
                return (
                    <React.Fragment>
                        <Header color='primary'>Convert to Opportunity</Header>
                        <ConvertToOpportunityForm
                            size='small'
                            account_id={account_id}
                            contact_id={contact_id}
                            onClose={onClose}
                            refresh={refresh}
                            onModify={(isModified) => { setIsConversionFormModified(isModified) }}
                            account_stage={account_stage}
                            master_source={master_source}
                            secondary_source={secondary_source}
                        />
                    </React.Fragment>
                )
            case 'customer':
                return (
                    <React.Fragment>
                        <Header color='primary'>
                            Convert <Popup trigger={<span className='Convert__account-name'>{account?.name || contactName}</span>} content={account?.name || contactName} inverted /> to Customer
                        </Header>
                        <ConvertAccountToCustomerForm
                            current_primary_contact_id={account?.primary_contact_id}
                            existing_account_id={account_id}
                            onClose={onClose}
                            onModify={(isModified) => { setIsConversionFormModified(isModified) }}
                            owner_id={contact_id}
                            pipeline_id={pipeline_id}
                            refresh={() => {
                                dispatch(contactActions.readContact(contact_id));
                            }}
                            size={"small"}
                        />
                    </React.Fragment>
                )
            case 'new-customer':
                return (
                    <React.Fragment>
                        <Header color='primary'>
                            Create Customer-Account
                        </Header>
                        <ConvertToNewCustomerForm
                            existing_account_id={account_id}
                            onClose={onClose}
                            onModify={(isModified) => { setIsConversionFormModified(isModified) }}
                            owner_id={contact_id}
                            pipeline_id={pipeline_id}
                            refresh={() => {
                                dispatch(contactActions.readContact(contact_id))
                            }}
                            size={"small"}
                            account_details={{
                                address: mainLocation || {
                                    label: '',
                                    street: '',
                                    additional_street: '',
                                    city: '',
                                    state: '',
                                    zip: '',
                                },
                                email: mainEmail?.email,
                                email_type: mainEmail?.email_type,
                                phone: mainPhone?.phone,
                                phone_type: mainPhone?.phone_type
                            }}
                        />
                    </React.Fragment>
                )
            default:
                return null;
        }
    }

    const isPrimaryContact = account?.primary_contact_id === contact_id;
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const isUpselling = useSelector((state) => {
        if (pipeline_id) {
            return state.quote.opportunityBeingUpsold === pipeline_id;
        }
        return false;
    });

    const [isSettlingOutstandingInvoices, setIsSettlingOutstandingInvoices] = useState(false);
    
    return (
        <React.Fragment>
            <Popup
                content={(
                    <CloseableContent onClose={onClose}>
                        {getPopupContent()}
                    </CloseableContent>
                )}
                open={conversionModalOpen}
                position='bottom center'
                style={{ zIndex: 5 }}
                context={modalRef}
                closeOnDocumentClick={!isConversionFormModified}
                onClose={onClose}
            />
            <Ref innerRef={modalRef}>
                <Dropdown 
                    as={Button} 
                    onClose={() => {setIsDropdownOpen(false)}} 
                    onOpen={async () => {
                        setIsDropdownOpen(true)
                        if (pipeline_id && canUpsell) {
                            dispatch(
                                quoteActions.getRecentQuoteByOpportunityId(pipeline_id, account_id ? { account_id } : {})
                            );
                        }
                    }} 
                    size='tiny' 
                    color='blue' 
                    className='ConvertDropdown'
                    text='Convert' 
                    loading={isUpselling}
                >
                    <Dropdown.Menu>
                        {!isUpselling && (
                            <>
                                {canConvertToLead && (
                                    <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.LEAD_EDIT} userRestrict assignmentIds={[(assignees?.owner_id || 0), (assignees?.coowner_id || 0)]}>
                                        <Dropdown.Item onClick={() => { setConversionModalOpen('lead') }}>
                                            Convert to Lead
                                        </Dropdown.Item>
                                    </RestrictedView>
                                )}
                                {canConvertToOpportunity && (
                                    <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.OPPORTUNITY_EDIT} userRestrict assignmentIds={[(assignees?.owner_id || 0), (assignees?.coowner_id || 0)]}>
                                        <Dropdown.Item onClick={() => { setConversionModalOpen('opportunity') }}>
                                            Convert to Opportunity
                                        </Dropdown.Item>
                                    </RestrictedView>
                                )}
                                {!account_is_a_customer ? (
                                    <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} userRestrict assignmentIds={[(assignees?.owner_id || 0), (assignees?.coowner_id || 0)]}>
                                        <Dropdown.Item 
                                            style={{ width: 160 }} 
                                            onClick={() => {
                                                if (!recentActiveQuoteId) {
                                                    setConversionModalOpen(account_id ? 'customer' : 'new-customer');
                                                }
                                            }}
                                            {...(recentActiveQuoteId ? ({ as: Dropdown, size: "tiny", trigger: "Convert Customer" }) : {})}
                                            text="Convert Customer"
                                        >
                                            {recentActiveQuoteId ? (
                                                <Dropdown.Menu>
                                                    <Dropdown.Item onClick={() => {
                                                        onEditQuote(recentActiveQuoteId);
                                                    }}>
                                                        Use Existing Quote
                                                    </Dropdown.Item>
                                                    <Dropdown.Item 
                                                        onClick={() => {
                                                            setConversionModalOpen(account_id ? "customer" : "new-customer")
                                                        }}
                                                    >
                                                        Don't Use Existing Quote
                                                    </Dropdown.Item>
                                                </Dropdown.Menu>
                                            ) : "Convert Customer"}
                                        </Dropdown.Item>
                                    </RestrictedView>
                                ) : (
                                    <>
                                        {account_stage === 'lead' ? (
                                            <Dropdown.Item disabled>
                                                {isDeactivatedCustomer ? "Reactivate Customer" : "Upsell Existing Customer"}
                                            </Dropdown.Item>
                                        ) : (
                                            <UpsellCustomerPopup 
                                                open={isDropdownOpen && isUpsellingFromQuote}
                                                position={"left center"}
                                                trigger={(
                                                    <RestrictedView type="disabled" userPermission={isDeactivatedCustomer ? USER_PERMISSIONS.ACCOUNT_EDIT : USER_PERMISSIONS.ORDERS_ADD} userRestrict assignmentIds={isDeactivatedCustomer ? [(assignees?.owner_id || 0), (assignees?.coowner_id || 0)] : false}>
                                                        <Dropdown.Item
                                                            style={{ width: 195 }}
                                                            onClick={isPausedCustomer ? () => {setIsSettlingOutstandingInvoices(true)} : onUpsell}
                                                        >
                                                            {isDeactivatedCustomer ? "Reactivate Customer" : "Upsell Customer"}
                                                        </Dropdown.Item>
                                                    </RestrictedView>
                                                )}
                                            />
                                        )}
                                        <ConvertNewCustomerPopup 
                                            open={isDropdownOpen && (isPrimaryContact)}
                                            isPrimaryContact={isPrimaryContact}
                                            account_id={account_id}
                                            trigger={(
                                                <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} userRestrict assignmentIds={[(assignees?.owner_id || 0), (assignees?.coowner_id || 0)]}>
                                                    <Dropdown.Item
                                                        disabled={isPrimaryContact}
                                                        style={{ width: 195 }}
                                                        onClick={() => {
                                                            if (!recentActiveQuoteId) {
                                                                setConversionModalOpen("new-customer");
                                                            }
                                                        }}
                                                        {...(recentActiveQuoteId ? ({ as: Dropdown, size: "tiny", trigger: "Convert New Customer" }) : {})}
                                                        text="Convert New Customer"
                                                    >
                                                        {recentActiveQuoteId ? (
                                                            <Dropdown.Menu>
                                                                <Dropdown.Item onClick={() => {
                                                                    onEditQuote(recentActiveQuoteId, ACTIONS.CONVERT_NEW_CUSTOMER);
                                                                }}>
                                                                    Use Existing Quote
                                                                </Dropdown.Item>
                                                                <Dropdown.Item onClick={() => {setConversionModalOpen("new-customer")}}>
                                                                    Don't Use Existing Quote
                                                                </Dropdown.Item>
                                                            </Dropdown.Menu>
                                                        ) : "Convert New Customer"}
                                                    </Dropdown.Item>
                                                </RestrictedView>
                                            )}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </Dropdown.Menu>
                </Dropdown>
            </Ref>
            {account_stage !== 'contact' && (
                <RestrictedView type="popupDisabled" userPermission={account_stage === "lead" ? USER_PERMISSIONS.LEAD_EDIT : USER_PERMISSIONS.OPPORTUNITY_EDIT} userRestrict assignmentIds={[(assignees?.owner_id || 0), (assignees?.coowner_id || 0)]}>
                    <Popup
                        trigger={(
                                <Button size='tiny' color='red'>
                                    Lost
                                </Button>
                        )}
                        content={(
                            <CloseableContent onClose={onCloseLostForm}>
                                <LostForm
                                    account_id={account_id}
                                    account_stage={account_stage}
                                    contact_id={contact_id}
                                    pipeline_id={pipeline_id}
                                    refresh={() => {
                                        dispatch(contactActions.readContact(contact_id));
                                    }}
                                    onModify={(isModified) => { setIsLostFormModified(isModified) }}
                                />
                            </CloseableContent>
                        )}
                        on='click'
                        open={lostFormOpen}
                        position='bottom center'
                        closeOnDocumentClick={!isLostFormModified}
                        onClose={() => {
                            if (!lostFormOpen) {
                                onCloseLostForm();
                            }
                        }}
                        onOpen={() => { setLostFormOpen(true) }}
                    />
                </RestrictedView>
            )}
            {isPausedCustomer && (
                <BulkActionsProvider>
                    <OutstandingInvoicesForm
                        isOpen={isSettlingOutstandingInvoices}
                        onClose={() => setIsSettlingOutstandingInvoices(false)}
                        customer_id={account_id}
                        formTitle={`Reactivate ${account?.account_name}`}
                        opportunity_id={pipeline_id}
                        refresh={() => {
                            dispatch(contactActions.readContact(contact_id));
                        }}
                    />
                </BulkActionsProvider>
            )}
        </React.Fragment>
    );
};

export default ConversionControls;

